'use client';
import { Input } from '@ui/components/moleculas';
import { IMessagePayload } from '@ui/components';
import { useFiles } from '@ui/hooks';
import { MAX_FILES } from '@ui/hooks/file/constants';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

const RichTextEditor = dynamic(
  () => import('@ui/components/ui/rte-editor/rte-editor').then((mod) => mod.RichTextEditor),
  { ssr: false },
);

const initialState: IMessagePayload = {
  send_email: false,
  files: {
    images: [],
  },
  name: '',
  description: '',
  post: true,
  type: 'post',
};

interface BasePostCreationFormProps {
  initialValues?: Partial<IMessagePayload>;
  onSubmit: (
    message: IMessagePayload,
    description: string,
    mentions: string[],
    clearEditorContent: () => void,
    clearMessage: () => void,
    discardFiles: () => void,
  ) => Promise<void>;
  isSubmitDisabled?: boolean;
  isLoading?: boolean;
  authorAvatarURL?: string;
  userType?: 'creator' | 'fan';
  currentUser?: 'creator' | 'fan';
  placeholder?: string;
  editorLabel?: string;
  buttonLabel?: string;
  showCancelButton?: boolean;
  enableSuggestions?: boolean;
  initialMention?: { id: string; label: string };
  enableSendEmailToggle?: boolean;
}

export const BasePostCreationForm = ({
  initialValues = {
    ...initialState,
  },
  onSubmit,

  isSubmitDisabled = false,
  isLoading = false,
  authorAvatarURL,
  userType = 'creator',
  currentUser = 'creator',
  placeholder = 'Compose content...',
  editorLabel = 'Create content',
  buttonLabel = 'Submit',
  showCancelButton = false,
  enableSuggestions = false,
  initialMention,
  enableSendEmailToggle = false,
}: BasePostCreationFormProps) => {
  const [message, setMessage] = useState<IMessagePayload>({
    send_email: false,
    files: { images: [] },
    name: '',
    description: '',
    post: true,
    type: 'post',
    ...initialValues,
  });
  const { files, onSelectFiles, deleteFile, onDiscardFiles } = useFiles();

  useEffect(() => {
    setMessage((state) => ({ ...state, files: { images: files } }));
  }, [files]);

  const onCancelEditor = () => {
    setMessage(initialState);
    onDiscardFiles();
  };

  const handleSendEmailToggle = (value: boolean) => {
    setMessage((prev) => ({ ...prev, send_email: value }));
  };

  const handleSubmit = (description: string, mentions: string[], clearEditor: () => void) =>
    onSubmit(
      message,
      description,
      mentions,
      clearEditor,
      () => setMessage(initialState),
      onDiscardFiles,
    );

  return (
    <div className="flex flex-col gap-1">
      <Input
        value={message.name}
        type="text"
        isHideError={true}
        placeholder="Title"
        onChange={(event: any) => setMessage({ ...message, name: event.target.value })}
      />
      <RichTextEditor
        deleteFile={deleteFile}
        onSelectFiles={onSelectFiles}
        isSubmitButtonDisabled={isSubmitDisabled || !Boolean(message.name)}
        authorAvatarURL={authorAvatarURL}
        onChange={handleSubmit}
        value={message.description || ''}
        userType={userType}
        currentUser={currentUser}
        files={files}
        isLoading={isLoading}
        exceededFileLimit={files.length >= MAX_FILES}
        placeholder={placeholder}
        editorLabel={editorLabel}
        buttonLabel={buttonLabel}
        maxFiles={MAX_FILES}
        showCancelButton={showCancelButton}
        onCancel={onCancelEditor}
        enableSuggestions={enableSuggestions}
        initialMention={initialMention}
        sendEmail={message.send_email}
        onToggleSendEmail={enableSendEmailToggle ? handleSendEmailToggle : undefined}
        editorClassName="min-h-[80px]"
      />
    </div>
  );
};
